import { Link } from 'react-router-dom'
import { Shield, ArrowRight, AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LandingNavigation } from '@/components/LandingNavigation'

interface FindingSummary {
  critical: number
  high: number
  medium: number
  low: number
}

interface ExampleScan {
  repository: string
  branch: string
  riskScore: number
  scannedAt: string
  findings: FindingSummary
  topFindings: string[]
}

const exampleScans: ExampleScan[] = [
  {
    repository: 'expressjs/express',
    branch: 'master',
    riskScore: 6.4,
    scannedAt: '2 hours ago',
    findings: {
      critical: 1,
      high: 3,
      medium: 8,
      low: 14,
    },
    topFindings: [
      'CVE-2024-29041: Open redirect vulnerability (CVSS 6.1)',
      'Prototype pollution in query string parser',
      'Missing security headers in default configuration',
    ],
  },
  {
    repository: 'lodash/lodash',
    branch: 'main',
    riskScore: 3.2,
    scannedAt: '1 day ago',
    findings: {
      critical: 0,
      high: 1,
      medium: 4,
      low: 9,
    },
    topFindings: [
      'CVE-2021-23337: Command injection via template function',
      'Outdated devDependencies with known vulnerabilities',
      'Missing Content Security Policy headers',
    ],
  },
  {
    repository: 'pallets/flask',
    branch: 'main',
    riskScore: 4.7,
    scannedAt: '3 days ago',
    findings: {
      critical: 0,
      high: 2,
      medium: 6,
      low: 11,
    },
    topFindings: [
      'CVE-2023-30861: Session cookie not invalidated on logout',
      'Unsafe deserialization in debug mode',
      'Dockerfile: last USER should not be root (DL3002)',
    ],
  },
]

function riskScoreVariant(score: number): 'destructive' | 'warning' | 'secondary' | 'success' {
  if (score >= 8) return 'destructive'
  if (score >= 5) return 'warning'
  if (score >= 3) return 'secondary'
  return 'success'
}

function findingIcon(level: keyof FindingSummary) {
  switch (level) {
    case 'critical':
      return <AlertCircle className="h-3.5 w-3.5 text-red-600" />
    case 'high':
      return <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
    case 'medium':
      return <Info className="h-3.5 w-3.5 text-yellow-500" />
    case 'low':
      return <CheckCircle className="h-3.5 w-3.5 text-blue-500" />
  }
}

function FindingCount({
  level,
  count,
}: {
  level: keyof FindingSummary
  count: number
}) {
  return (
    <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
      {findingIcon(level)}
      <span className="font-semibold">{count}</span>
      <span className="text-gray-500 dark:text-gray-400 capitalize">{level}</span>
    </div>
  )
}

export function ExamplesPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <LandingNavigation />

      {/* Hero */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-6 flex justify-center">
              <Shield className="h-12 w-12 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              See RiskLens in Action
            </h1>
            <p className="mt-4 text-lg leading-8 text-gray-600 dark:text-gray-300">
              Explore sample scan results from popular open-source repositories.
              RiskLens surfaces real vulnerabilities, misconfigurations, and
              dependency risks — so you can fix them before they become incidents.
            </p>
          </div>
        </div>
      </div>

      {/* Example scan cards */}
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {exampleScans.map((scan) => (
            <Card
              key={scan.repository}
              className="flex flex-col hover:shadow-md transition-shadow"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base font-mono truncate">
                      {scan.repository}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Branch: {scan.branch} &bull; {scan.scannedAt}
                    </CardDescription>
                  </div>
                  <Badge variant={riskScoreVariant(scan.riskScore)} className="shrink-0">
                    {scan.riskScore.toFixed(1)}/10
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="flex flex-col flex-1 gap-4">
                {/* Finding counts */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {(
                    ['critical', 'high', 'medium', 'low'] as (keyof FindingSummary)[]
                  ).map((level) => (
                    <FindingCount
                      key={level}
                      level={level}
                      count={scan.findings[level]}
                    />
                  ))}
                </div>

                {/* Top findings preview */}
                <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Sample Findings
                  </p>
                  <ul className="space-y-1">
                    {scan.topFindings.map((finding) => (
                      <li
                        key={finding}
                        className="text-xs text-gray-600 dark:text-gray-300 flex items-start gap-1.5"
                      >
                        <span className="mt-0.5 shrink-0 text-gray-400">&bull;</span>
                        {finding}
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-auto"
                  disabled
                  title="Sign up to view full reports"
                >
                  View Sample Report
                  <ArrowRight className="h-3.5 w-3.5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-blue-600">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ready to scan your own repos?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-blue-100">
              Sign up for free and run your first security scan in minutes. No
              credit card required.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/register">
                  Sign Up Free
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button
                variant="ghost"
                className="text-white hover:text-white hover:bg-blue-700"
                asChild
              >
                <Link to="/login">Sign In</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
